import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Activity,
  Database,
  Zap,
  TrendingUp,
  Clock,
  DollarSign,
  AlertCircle,
  CheckCircle,
  BarChart3,
  FileText,
  Bot,
  Cpu,
  RefreshCw,
} from 'lucide-react';

export function RAGDashboard() {
  const navigate = useNavigate();
  const [timeRange, setTimeRange] = useState('30d');
  const [isRefreshing, setIsRefreshing] = useState(false);
  const refreshTimerRef = useRef<number | null>(null);

  const handleRefresh = () => {
    setIsRefreshing(true);
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
    }
    refreshTimerRef.current = window.setTimeout(() => setIsRefreshing(false), 1000);
  };

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (refreshTimerRef.current) {
        clearTimeout(refreshTimerRef.current);
      }
    };
  }, []);

  const metrics = {
    totalQueries: 12547,
    avgResponseTime: '5.3s',
    successRate: 98.5,
    totalCost: 234.56,
    documentsIndexed: 1840,
    vectorDbSize: '2.4 GB',
    mcpServers: 7,
    avgRelevanceScore: 0.87,
  };

  // Fix for dynamic Tailwind classes - use static classes with mapping
  const colorClasses: Record<string, { bg: string; text: string; bgLight: string }> = {
    blue: { bg: 'bg-blue-100', text: 'text-blue-600', bgLight: 'bg-blue-50' },
    purple: { bg: 'bg-fk-orange-vibrant100', text: 'text-#ea7500', bgLight: 'bg-fk-orange-vibrant50' },
    green: { bg: 'bg-green-100', text: 'text-green-600', bgLight: 'bg-green-50' },
    orange: { bg: 'bg-orange-100', text: 'text-orange-600', bgLight: 'bg-orange-50' },
  };

  const pipelineStages = [
    {
      stage: 1,
      name: 'Query Analyzer',
      avgTime: '800ms',
      percentage: 15,
      color: 'blue',
      description: 'Claude analyzes query and selects MCP servers',
    },
    {
      stage: 2,
      name: 'MCP Router',
      avgTime: '1,500ms',
      percentage: 28,
      color: 'purple',
      description: 'Parallel retrieval from 7 MCP servers',
    },
    {
      stage: 3,
      name: 'Reranker',
      avgTime: '600ms',
      percentage: 11,
      color: 'green',
      description: 'Hybrid scoring + Cohere reranking',
    },
    {
      stage: 4,
      name: 'LLM Generation',
      avgTime: '2,000ms',
      percentage: 38,
      color: 'orange',
      description: 'Context merger + Claude generation',
    },
  ];

  const recentQueries = [
    {
      id: 1,
      query: 'What are the key differences between RAG and Agentic RAG?',
      timestamp: '2 min ago',
      responseTime: '1.1s',
      relevance: 0.92,
      status: 'success',
    },
    {
      id: 2,
      query: 'Explain Multi-Agent RAG architecture',
      timestamp: '5 min ago',
      responseTime: '1.4s',
      relevance: 0.89,
      status: 'success',
    },
    {
      id: 3,
      query: 'How to configure MCP servers?',
      timestamp: '8 min ago',
      responseTime: '0.9s',
      relevance: 0.85,
      status: 'success',
    },
    {
      id: 4,
      query: 'What is ReACT planning?',
      timestamp: '12 min ago',
      responseTime: '2.1s',
      relevance: 0.78,
      status: 'warning',
    },
  ];

  const mcpServersStatus = [
    { name: 'MCP Pinecone', status: 'healthy', uptime: '99.9%', avgLatency: '210ms', weight: 0.85 },
    { name: 'MCP PDF Content', status: 'healthy', uptime: '99.8%', avgLatency: '180ms', weight: 0.80 },
    { name: 'MCP OE Database', status: 'healthy', uptime: '100%', avgLatency: '95ms', weight: 0.95 },
    { name: 'MCP Regulations', status: 'healthy', uptime: '99.7%', avgLatency: '320ms', weight: 1.00 },
    { name: 'MCP Previous Exams', status: 'healthy', uptime: '99.5%', avgLatency: '150ms', weight: 0.90 },
    { name: 'MCP Glossary', status: 'healthy', uptime: '100%', avgLatency: '85ms', weight: 0.70 },
    { name: 'MCP ANBIMA API', status: 'warning', uptime: '97.2%', avgLatency: '450ms', weight: 0.95 },
  ];

  const systemHealth = [
    { component: 'Query Analyzer (Claude)', status: 'healthy', uptime: '99.9%' },
    { component: 'MCP Router', status: 'healthy', uptime: '99.8%' },
    { component: 'Reranker (Cohere)', status: 'healthy', uptime: '99.7%' },
    { component: 'LLM Generator (Claude)', status: 'healthy', uptime: '100%' },
    { component: 'Validation Engine', status: 'healthy', uptime: '100%' },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">RAG System Dashboard</h1>
          <p className="text-sm text-gray-600 mt-1">
            Monitor your Retrieval-Augmented Generation system performance and health
          </p>
        </div>
        <div className="flex items-center space-x-3">
          <select
            value={timeRange}
            onChange={(e) => setTimeRange(e.target.value)}
            className="px-4 py-2 bg-white border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-primary-500"
          >
            <option value="24h">Last 24 hours</option>
            <option value="7d">Last 7 days</option>
            <option value="30d">Last 30 days</option>
            <option value="90d">Last 90 days</option>
          </select>
          <button
            onClick={handleRefresh}
            disabled={isRefreshing}
            className="px-4 py-2 bg-primary-600 text-white rounded-xl text-sm font-medium hover:bg-primary-700 disabled:bg-gray-400 flex items-center"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${isRefreshing ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-4 gap-6">
        <div
          className="bg-white rounded-xl border border-gray-200 p-6 hover:shadow-sm transition-shadow cursor-pointer"
          onClick={() => navigate('/rag/executions')}
        >
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 bg-blue-50 rounded-xl">
              <Activity className="w-6 h-6 text-blue-600" />
            </div>
            <span className="text-xs font-medium text-green-600 bg-green-50 px-2 py-1 rounded-full">
              +12.3%
            </span>
          </div>
          <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
            Total Queries
          </p>
          <p className="text-3xl font-bold text-gray-900">{metrics.totalQueries.toLocaleString()}</p>
          <p className="text-xs text-gray-500 mt-1">Last {timeRange === '24h' ? '24 hours' : timeRange === '7d' ? '7 days' : timeRange === '90d' ? '90 days' : '30 days'}</p>
        </div>

        <div
          className="bg-white rounded-xl border border-gray-200 p-6 hover:shadow-sm transition-shadow cursor-pointer"
          onClick={() => navigate('/rag/executions')}
        >
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 bg-fk-orange-vibrant50 rounded-xl">
              <Clock className="w-6 h-6 text-#ea7500" />
            </div>
            <span className="text-xs font-medium text-green-600 bg-green-50 px-2 py-1 rounded-full">
              -8%
            </span>
          </div>
          <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
            Avg Response Time
          </p>
          <p className="text-3xl font-bold text-gray-900">{metrics.avgResponseTime}</p>
          <p className="text-xs text-gray-500 mt-1">Target: &lt;2s</p>
        </div>

        <div
          className="bg-white rounded-xl border border-gray-200 p-6 hover:shadow-sm transition-shadow cursor-pointer"
          onClick={() => navigate('/rag/validation')}
        >
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 bg-green-50 rounded-xl">
              <CheckCircle className="w-6 h-6 text-green-600" />
            </div>
            <span className="text-xs font-medium text-green-600 bg-green-50 px-2 py-1 rounded-full">
              +0.5%
            </span>
          </div>
          <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
            Success Rate
          </p>
          <p className="text-3xl font-bold text-gray-900">{metrics.successRate}%</p>
          <p className="text-xs text-gray-500 mt-1">High quality responses</p>
        </div>

        <div
          className="bg-white rounded-xl border border-gray-200 p-6 hover:shadow-sm transition-shadow cursor-pointer"
          onClick={() => navigate('/rag/costs')}
        >
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 bg-amber-50 rounded-xl">
              <DollarSign className="w-6 h-6 text-amber-600" />
            </div>
            <span className="text-xs font-medium text-red-600 bg-red-50 px-2 py-1 rounded-full">
              +18%
            </span>
          </div>
          <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
            Total Cost
          </p>
          <p className="text-3xl font-bold text-gray-900">${metrics.totalCost}</p>
          <p className="text-xs text-gray-500 mt-1">This month</p>
        </div>
      </div>

      {/* Secondary Metrics */}
      <div className="grid grid-cols-4 gap-6">
        <div className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-xl border border-blue-100 p-6">
          <div className="flex items-center space-x-3">
            <Database className="w-8 h-8 text-blue-600" />
            <div>
              <p className="text-sm text-gray-600">Documents Indexed</p>
              <p className="text-2xl font-bold text-gray-900">{metrics.documentsIndexed}</p>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-xl border border-orange-100 p-6">
          <div className="flex items-center space-x-3">
            <FileText className="w-8 h-8 text-#ea7500" />
            <div>
              <p className="text-sm text-gray-600">Vector DB Size</p>
              <p className="text-2xl font-bold text-gray-900">{metrics.vectorDbSize}</p>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl border border-green-100 p-6">
          <div className="flex items-center space-x-3">
            <Bot className="w-8 h-8 text-green-600" />
            <div>
              <p className="text-sm text-gray-600">MCP Servers</p>
              <p className="text-2xl font-bold text-gray-900">{metrics.mcpServers}</p>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-orange-50 to-amber-50 rounded-xl border border-orange-100 p-6">
          <div className="flex items-center space-x-3">
            <TrendingUp className="w-8 h-8 text-orange-600" />
            <div>
              <p className="text-sm text-gray-600">Avg Relevance</p>
              <p className="text-2xl font-bold text-gray-900">{(metrics.avgRelevanceScore * 100).toFixed(0)}%</p>
            </div>
          </div>
        </div>
      </div>

      {/* 4-Stage Pipeline Visualization */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-primary-50 to-blue-50">
          <div className="flex items-center space-x-3">
            <Zap className="w-5 h-5 text-primary-600" />
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Agentic RAG Pipeline (4 Stages)</h3>
              <p className="text-xs text-gray-600">Average latency per stage</p>
            </div>
          </div>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-4 gap-4">
            {pipelineStages.map((stage) => {
              const colors = colorClasses[stage.color as keyof typeof colorClasses];
              return (
              <div key={stage.stage} className="relative">
                <div className="bg-gray-50 rounded-lg border border-gray-200 p-4 hover:shadow-md transition-all cursor-pointer"
                     onClick={() => navigate('/rag/traces')}>
                  <div className="flex items-center justify-between mb-3">
                    <div className={`w-10 h-10 ${colors.bg} rounded-lg flex items-center justify-center`}>
                      <span className={`text-lg font-bold ${colors.text}`}>{stage.stage}</span>
                    </div>
                    <span className="text-xs font-semibold text-gray-500">{stage.percentage}%</span>
                  </div>
                  <h4 className="text-sm font-semibold text-gray-900 mb-1">{stage.name}</h4>
                  <p className="text-xs text-gray-600 mb-3 line-clamp-2">{stage.description}</p>
                  <div className="flex items-center justify-between">
                    <span className={`text-sm font-bold ${colors.text}`}>{stage.avgTime}</span>
                    <Clock className="w-4 h-4 text-gray-400" />
                  </div>
                </div>
                {stage.stage < 4 && (
                  <div className="absolute top-1/2 -right-2 transform -translate-y-1/2 z-10">
                    <div className="w-4 h-0.5 bg-gray-300" />
                    <div className="absolute -right-1 -top-1 w-2 h-2 border-t-2 border-r-2 border-gray-300 transform rotate-45" />
                  </div>
                )}
              </div>
            );
            })}
          </div>
        </div>
      </div>

      {/* MCP Servers Status */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-orange-50 to-orange-100">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <Database className="w-5 h-5 text-#ea7500" />
              <div>
                <h3 className="text-lg font-semibold text-gray-900">MCP Servers (7 Knowledge Sources)</h3>
                <p className="text-xs text-gray-600">Model Context Protocol servers for retrieval</p>
              </div>
            </div>
            <span className="text-xs font-medium text-green-600 bg-green-50 px-2 py-1 rounded-full">
              6/7 Healthy
            </span>
          </div>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-2 gap-3">
            {mcpServersStatus.map((server, idx) => (
              <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200 hover:shadow-sm transition-all">
                <div className="flex items-center space-x-3 flex-1">
                  {server.status === 'healthy' ? (
                    <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
                  ) : (
                    <AlertCircle className="w-5 h-5 text-yellow-600 flex-shrink-0" />
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-gray-900 truncate">{server.name}</p>
                    <div className="flex items-center space-x-3 text-xs text-gray-500 mt-0.5">
                      <span>Latency: {server.avgLatency}</span>
                      <span>•</span>
                      <span>Weight: {server.weight}</span>
                    </div>
                  </div>
                </div>
                <span className={`px-2 py-0.5 text-xs font-semibold rounded ${
                  server.status === 'healthy'
                    ? 'bg-green-100 text-green-700'
                    : 'bg-yellow-100 text-yellow-700'
                }`}>
                  {server.uptime}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-6">
        {/* Recent Queries */}
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <BarChart3 className="w-5 h-5 text-gray-600" />
                <h3 className="text-lg font-semibold text-gray-900">Recent Queries</h3>
              </div>
              <button
                onClick={() => navigate('/rag/executions')}
                className="text-sm text-primary-600 hover:text-primary-700 font-medium"
              >
                View All
              </button>
            </div>
          </div>
          <div className="divide-y divide-gray-100">
            {recentQueries.map((query) => (
              <div key={query.id} className="p-4 hover:bg-gray-50 transition-colors">
                <div className="flex items-start justify-between mb-2">
                  <p className="text-sm text-gray-900 flex-1 mr-4">{query.query}</p>
                  <span
                    className={`px-2 py-1 text-xs font-medium rounded-full ${
                      query.status === 'success'
                        ? 'bg-green-100 text-green-700'
                        : 'bg-yellow-100 text-yellow-700'
                    }`}
                  >
                    {query.relevance.toFixed(2)}
                  </span>
                </div>
                <div className="flex items-center space-x-4 text-xs text-gray-500">
                  <span className="flex items-center">
                    <Clock className="w-3 h-3 mr-1" />
                    {query.timestamp}
                  </span>
                  <span className="flex items-center">
                    <Zap className="w-3 h-3 mr-1" />
                    {query.responseTime}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* System Health */}
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <Cpu className="w-5 h-5 text-gray-600" />
                <h3 className="text-lg font-semibold text-gray-900">System Health</h3>
              </div>
              <span className="text-xs font-medium text-green-600 bg-green-50 px-2 py-1 rounded-full">
                All Systems Operational
              </span>
            </div>
          </div>
          <div className="divide-y divide-gray-100">
            {systemHealth.map((component, idx) => (
              <div key={idx} className="p-4 hover:bg-gray-50 transition-colors">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    {component.status === 'healthy' ? (
                      <CheckCircle className="w-5 h-5 text-green-600" />
                    ) : (
                      <AlertCircle className="w-5 h-5 text-yellow-600" />
                    )}
                    <div>
                      <p className="text-sm font-medium text-gray-900">{component.component}</p>
                      <p className="text-xs text-gray-500">Uptime: {component.uptime}</p>
                    </div>
                  </div>
                  <span
                    className={`px-2.5 py-1 text-xs font-semibold rounded-full ${
                      component.status === 'healthy'
                        ? 'bg-green-100 text-green-700 border border-green-200'
                        : 'bg-yellow-100 text-yellow-700 border border-yellow-200'
                    }`}
                  >
                    {component.status === 'healthy' ? 'Healthy' : 'Warning'}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="bg-gradient-to-r from-primary-50 to-blue-50 rounded-xl border border-primary-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h3>
        <div className="grid grid-cols-4 gap-4">
          <button
            onClick={() => navigate('/materials')}
            className="bg-white rounded-lg border border-gray-200 p-4 hover:shadow-md transition-all text-left"
          >
            <Database className="w-8 h-8 text-blue-600 mb-2" />
            <p className="text-sm font-semibold text-gray-900">Upload Materials</p>
            <p className="text-xs text-gray-500 mt-1">Add new documents to index</p>
          </button>
          <button
            onClick={() => navigate('/rag/agents')}
            className="bg-white rounded-lg border border-gray-200 p-4 hover:shadow-md transition-all text-left"
          >
            <Bot className="w-8 h-8 text-#ea7500 mb-2" />
            <p className="text-sm font-semibold text-gray-900">Configure Agents</p>
            <p className="text-xs text-gray-500 mt-1">Manage AI agents</p>
          </button>
          <button
            onClick={() => navigate('/rag/playground')}
            className="bg-white rounded-lg border border-gray-200 p-4 hover:shadow-md transition-all text-left"
          >
            <Zap className="w-8 h-8 text-green-600 mb-2" />
            <p className="text-sm font-semibold text-gray-900">Test Queries</p>
            <p className="text-xs text-gray-500 mt-1">Try the playground</p>
          </button>
          <button
            onClick={() => navigate('/rag/costs')}
            className="bg-white rounded-lg border border-gray-200 p-4 hover:shadow-md transition-all text-left"
          >
            <BarChart3 className="w-8 h-8 text-orange-600 mb-2" />
            <p className="text-sm font-semibold text-gray-900">View Analytics</p>
            <p className="text-xs text-gray-500 mt-1">Detailed performance metrics</p>
          </button>
        </div>
      </div>
    </div>
  );
}
